import { useState, FormEvent, useEffect } from 'react';
import { TemplateSelector } from './TemplateSelector';
import { Template, apiService } from '../services/api';

interface PromptFormProps {
  onSubmit: (prompt: string, templateId?: string, includeCoverAndEnd?: boolean, pageCount?: number) => void;
  isLoading?: boolean;
  disabled?: boolean;
}

const PROMPT_HISTORY_KEY = 'aippt_prompt_history_v1';
const MAX_PROMPT_HISTORY = 8;
const MIN_PROMPT_LENGTH = 10;
const MAX_PROMPT_LENGTH = 50000;

export function PromptForm({ onSubmit, isLoading, disabled }: PromptFormProps) {
  const [prompt, setPrompt] = useState('');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState('classic-minimal');
  const [promptHistory, setPromptHistory] = useState<string[]>([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(true);
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [includeCoverAndEnd, setIncludeCoverAndEnd] = useState(true);
  const [pageCount, setPageCount] = useState<number>(5);

  useEffect(() => {
    // 读取提示词历史
    try {
      const saved = localStorage.getItem(PROMPT_HISTORY_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setPromptHistory(parsed.filter((item): item is string => typeof item === 'string'));
        }
      }
    } catch (readError) {
      console.warn('Failed to read prompt history:', readError);
    }

    // 获取模板列表（独立于提示词历史）
    const fetchTemplates = async () => {
      try {
        const fetchedTemplates = await apiService.getTemplates();
        setTemplates(fetchedTemplates);
      } catch (err) {
        console.error('Failed to fetch templates:', err);
      } finally {
        setIsLoadingTemplates(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    if (prompt.length < MIN_PROMPT_LENGTH) {
      setError(`提示词至少需要 ${MIN_PROMPT_LENGTH} 个字符`);
      return;
    }

    if (prompt.length > MAX_PROMPT_LENGTH) {
      setError(`提示词不能超过 ${MAX_PROMPT_LENGTH} 个字符`);
      return;
    }

    setError('');
    const nextHistory = [prompt.trim(), ...promptHistory.filter((item) => item !== prompt.trim())]
      .slice(0, MAX_PROMPT_HISTORY);
    setPromptHistory(nextHistory);
    localStorage.setItem(PROMPT_HISTORY_KEY, JSON.stringify(nextHistory));
    onSubmit(prompt, selectedTemplate, includeCoverAndEnd, pageCount);
  };

  const selectedTemplateData = templates.find(t => t.id === selectedTemplate);

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* 模板选择器 */}
      {templates.length > 0 ? (
        <TemplateSelector
          templates={templates}
          selectedTemplate={selectedTemplate}
          onSelect={setSelectedTemplate}
        />
      ) : isLoadingTemplates ? (
        <div className="text-center text-slate-500 py-8">加载模板中...</div>
      ) : (
        <div className="text-center text-red-500 py-8">无法加载模板</div>
      )}

      {/* 页数控制 & 封面封尾开关 */}
      <div className="flex gap-4">
        {/* 页数控制 */}
        <div className="flex-1 flex items-center justify-between px-4 py-3 rounded-xl border-2 border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: `${selectedTemplateData?.colors?.primary || '#3B82F6'}15`,
                color: selectedTemplateData?.colors?.primary || '#3B82F6',
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-700">页数</div>
              <div className="text-xs text-slate-500">内容页数量</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPageCount(Math.max(1, pageCount - 1))}
              disabled={isLoading || pageCount <= 1}
              className="w-7 h-7 rounded-lg border border-slate-300 flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              -
            </button>
            <span className="w-8 text-center text-sm font-bold text-slate-700">{pageCount}</span>
            <button
              type="button"
              onClick={() => setPageCount(Math.min(30, pageCount + 1))}
              disabled={isLoading || pageCount >= 30}
              className="w-7 h-7 rounded-lg border border-slate-300 flex items-center justify-center text-slate-500 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              +
            </button>
          </div>
        </div>

        {/* 封面封尾开关 */}
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border-2 border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: `${selectedTemplateData?.colors?.primary || '#3B82F6'}15`,
                color: selectedTemplateData?.colors?.primary || '#3B82F6',
              }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className='pr-8'>
              <div className="text-sm font-semibold text-slate-700">包含封面和封尾</div>
              <div className="text-xs text-slate-500">自动生成标题页和结束页</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIncludeCoverAndEnd(!includeCoverAndEnd)}
            className={`
              relative inline-flex h-7 w-[52px] items-center rounded-full transition-colors duration-200
              ${includeCoverAndEnd
                ? 'opacity-100 cursor-pointer'
                : 'opacity-50 cursor-pointer'
              }
            `}
            style={{
              backgroundColor: includeCoverAndEnd
                ? (selectedTemplateData?.colors?.primary || '#3B82F6')
                : '#CBD5E1',
            }}
            disabled={isLoading}
          >
            <span
              className={`
                inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200
                ${includeCoverAndEnd ? 'translate-x-7' : 'translate-x-1'}
              `}
            />
          </button>
        </div>
      </div>

      {/* 输入区域 */}
      <div className="relative">
        <label htmlFor="prompt" className="block text-sm font-semibold text-slate-700 mb-3">
          描述你想创建的演示文稿
        </label>
        <div className="relative">
          <textarea
            id="prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder="例如：生成一份关于人工智能发展趋势的演示文稿，包含技术原理、应用场景和未来展望..."
            className={`
              w-full h-40 px-6 py-4 rounded-2xl border-2 resize-none
              transition-all duration-200
              placeholder:text-slate-400
              ${isFocused ? 'border-blue-500 shadow-lg shadow-blue-500/20' : 'border-slate-200 hover:border-slate-300'}
              ${error ? 'border-red-300' : ''}
            `}
            disabled={isLoading}
          />
          {/* 字数计数 */}
          <div className="absolute bottom-4 right-4 text-xs font-medium px-2 py-1 rounded-md"
            style={{
              backgroundColor: isFocused ? `${selectedTemplateData?.colors?.primary || '#3B82F6'}10` : 'transparent',
              color: isFocused ? selectedTemplateData?.colors?.primary || '#3B82F6' : '#94A3B8',
            }}
          >
            {prompt.length} / {MAX_PROMPT_LENGTH}
          </div>
        </div>
        {error && (
          <p className="mt-2 text-sm text-red-600 font-medium flex items-center gap-1">
            <span>⚠</span>
            {error}
          </p>
        )}
      </div>

      {/* 历史记录 */}
      {promptHistory.length > 0 && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700">最近使用</h3>
            <button
              type="button"
              onClick={() => {
                setPromptHistory([]);
                localStorage.removeItem(PROMPT_HISTORY_KEY);
              }}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium transition-colors"
            >
              清空全部
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {promptHistory.map((item, index) => (
              <button
                key={`${index}-${item.slice(0, 12)}`}
                type="button"
                onClick={() => setPrompt(item)}
                className="group w-[200px] px-4 py-2 rounded-full border-2 border-slate-200 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-all text-left"
              >
                <span className="truncate block">{item}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 提交按钮 */}
      <button
        type="submit"
        disabled={isLoading || disabled || prompt.length < MIN_PROMPT_LENGTH}
        className={`
          w-full py-4 px-8 rounded-2xl font-semibold text-white
          transform transition-all duration-200
          hover:scale-[1.01] active:scale-[0.99]
          disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
          shadow-lg hover:shadow-xl
        `}
        style={{
          background: isLoading || disabled || prompt.length < MIN_PROMPT_LENGTH
            ? 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)'
            : `linear-gradient(135deg, ${selectedTemplateData?.colors?.primary || '#3B82F6'} 0%, ${selectedTemplateData?.colors?.accent || '#8B5CF6'} 100%)`,
        }}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12c0 2.34-.415 4.56-1.167 6.395L12 20.828l2.833-2.433C15.585 16.56 16 14.34 16 12c0-2.34-.415-4.56-1.167-6.395z" />
            </svg>
            生成中...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            开始生成 PPT
          </span>
        )}
      </button>
    </form>
  );
}
